import { Chip, CircularProgress, List, ListItem, ListItemText, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Pagination from '@mui/material/Pagination';
import { ChangeEvent, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBlocker, useLocation, useParams } from 'react-router-dom';
import {
  CHECKBOX,
  CLOSED,
  FREE_TEXT,
  IN_PROGRESS,
  LOCK,
  MIN_PERCENTAGE_FILLED,
  RADIO,
  REFRESH,
  REFRESH_OR_SAVE,
  SUBMITTED,
  UNLOCK,
  verticalScrollStyle,
} from '../../constants';
import socketHandler from '../../helpers/socket';
import {
  compareData,
  generateRandomString,
  getSurveyItem,
  handleActiveSection,
  handleOnScroll,
  validateUserSurveyAnswers,
} from '../../helpers/surveyUtil';
import { ChapterProps, InterrelatedFieldProps, QuestionAnswer, SectionProps, TabularAnswer } from '../../models/global';
import { State } from '../../models/state';
import {
  createOrganizationSurveyAnswers,
  getOrganizationSurvey,
  handleSurveyLock,
  submitSurvey,
} from '../../store/actions/organizationSurvey';
import { getStaticValues, getSurveySeriesById } from '../../store/actions/survey';
import useWakeupDetection from '../../useWakeUpDetection';
import Confirmation from '../Confirmation/Confirmation';
import { ExportModal } from '../ExportModal/ExportModal';
import Button from '../FormComponents/Button';
import Dropdown from '../FormComponents/Dropdown';
import Switch from '../FormComponents/Switch';
import NoData from '../NoData/NoData';
import NoteContent from '../NoteContent/NoteContent';
import '../SurveyForm/SurveyForm.scss';
import ToastMessage from '../ToastMessage/ToastMessage';
import TOCButton from '../TOCButton/TOCButton';
import SaveOrRefresh from './SaveOrRefresh';
import SaveOrResetChanges from './SaveOrResetChanges';
import UserFieldBlock from './UserFieldBlock';

function UserSurvey() {
  const submitErrorMessage = `You have filled less than ${MIN_PERCENTAGE_FILLED}% of the survey.`;
  const { id, seriesId, organizationId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const { languages, surveySeries } = useSelector((state: State) => state.survey);
  const { language: userLanguage, organization, id: userId, isAdmin } = useSelector((state: State) => state.auth.user);
  const [chapters, setChapters] = useState<ChapterProps[]>([
    {
      id: generateRandomString(true),
      name: 'Chapter 1',
      sections: [
        {
          id: generateRandomString(true),
          name: 'Section 1',
          fieldBlocks: [{ id: generateRandomString(true), question: '', fieldType: '', codeId: null }],
        },
      ],
      sectionsPaginated: [],
      active: true,
    },
  ]);
  const [questionAnswers, setQuestionAnswers] = useState<QuestionAnswer[]>([]);
  const [tabularAnswers, setTabularAnswers] = useState<TabularAnswer[]>([]);
  const [DBQuestionAnswers, setDBQuestionAnswers] = useState<QuestionAnswer[]>([]);
  const [DBTabularAnswers, setDBTabularAnswers] = useState<TabularAnswer[]>([]);
  const chaptersRef = useRef(chapters);
  const [surveyName, setSurveyName] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [language, setLanguage] = useState('English');
  const TOCClickedRef = useRef(false);
  const [status, setStatus] = useState('');
  const [organizationSurvey, setOrganizationSurvey] = useState<any>({});
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isLockedBy, setIsLockedBy] = useState('');
  const [filling, setFilling] = useState(false);
  const [saveOrResetChangesPopup, setSaveOrResetChangesPopup] = useState<any>();
  const [saveOrRefreshPopup, setSaveOrRefreshPopup] = useState<string>('');
  const [confirmationPopupText, setConfirmationPopupText] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showLastSurvey, setShowLastSurvey] = useState(false);
  const [pages, setPages] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [toastBackgroundHidden, setToastBackgroundHidden] = useState(false);
  const [lockedSectionsByOthers, setLockedSectionsByOthers] = useState<string[]>([]);
  const [showToc, setShowToc] = useState(true);
  const [showExportPopup, setShowExportPopup] = useState(false);
  const socketRef = useRef<any>();
  const lockedSection = useRef<string>();
  const lockedSectionsByOthersRef = useRef<string[]>([]);
  const questionAnswersRef = useRef<QuestionAnswer[]>([]);
  const tabularAnswersRef = useRef<TabularAnswer[]>([]);
  const DBQuestionAnswersRef = useRef<QuestionAnswer[]>([]);
  const DBTabularAnswersRef = useRef<TabularAnswer[]>([]);
  const isSameData = compareData(questionAnswers, DBQuestionAnswers) && compareData(tabularAnswers, DBTabularAnswers);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => !isSameData && currentLocation.pathname !== nextLocation.pathname,
  );

  const changePage = () => {
    if (saveOrResetChangesPopup) {
      const { chapterId, sectionId } = saveOrResetChangesPopup;
      handleScrollOnClick(chapterId, sectionId);
      setSaveOrResetChangesPopup(null);
    }
  };

  const handleSocketConnection = () => {
    socketRef.current = socketHandler();
    socketRef.current.on('section-lock', ({ sectionId }) => {
      const newData = Array.from(new Set([...lockedSectionsByOthersRef.current, sectionId]));
      setLockedSectionsByOthers(newData);
      lockedSectionsByOthersRef.current = newData;
    });
    socketRef.current.on('section-unlock', ({ sectionId }) => {
      const newData = lockedSectionsByOthersRef.current.filter(el => el !== sectionId);
      setLockedSectionsByOthers(newData);
      lockedSectionsByOthersRef.current = newData;
    });
    socketRef.current.on('trigger-survey-update', () => {
      getSurveyData(false, true);
    });
    socketRef.current.on('disconnect', () => {
      setTimeout(() => {
        socketRef.current.connect();
      }, 5000);
    });
  };

  const handleDataChangesAfterOnline = () => {
    const isSameData =
      compareData(questionAnswersRef.current, DBQuestionAnswersRef.current) &&
      compareData(tabularAnswersRef.current, DBTabularAnswersRef.current);
    if (!isSameData) {
      setSaveOrRefreshPopup(REFRESH_OR_SAVE);
    } else {
      const section = document.getElementById(lockedSection.current);
      section?.classList.remove('highlighted-section');
      getSurveyData(false, true);
      setActiveSection(null);
      lockedSection.current = null;
    }
  };

  const wakeup = () => {
    if (lockedSection.current) {
      dispatch(
        getOrganizationSurvey({ id }, res => {
          if (res.sectionLocks.includes(lockedSection.current)) {
            setSaveOrRefreshPopup(REFRESH);
          } else {
            handleDataChangesAfterOnline();
          }
        }),
      );
    } else {
      handleDataChangesAfterOnline();
    }
  };

  useWakeupDetection(wakeup);

  useEffect(() => {
    handleSocketConnection();
    const handleOnline = () => {
      setToastType('success');
      setToast('You are now online.');
      handleSocketConnection();
      wakeup();
    };
    const handleOffline = () => {
      setToastType('error');
      setToast('You are offline.');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    const onPageLeave = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      if (activeSection) {
        unlockSection(activeSection);
      }
    };
    if (!isAdmin) {
      window.addEventListener('beforeunload', onPageLeave);
    }

    return () => {
      window.removeEventListener('beforeunload', onPageLeave);
    };
  }, [isAdmin]);

  useEffect(() => {
    questionAnswersRef.current = questionAnswers;
    tabularAnswersRef.current = tabularAnswers;
  }, [questionAnswers, tabularAnswers]);

  useEffect(() => {
    DBQuestionAnswersRef.current = DBQuestionAnswers;
    DBTabularAnswersRef.current = DBTabularAnswers;
  }, [DBQuestionAnswers, DBTabularAnswers]);

  useEffect(() => {
    changePage();
  }, [organizationSurvey, questionAnswers, tabularAnswers]);

  useEffect(() => {
    if (isLockedBy && isLockedBy === userId) {
      setFilling(true);
    }
  }, [isLockedBy, userId]);

  const handleLanguage = (lang: string) => {
    setLanguage(lang);
  };

  useEffect(() => {
    if (isAdmin) {
      handleLanguage('English');
    } else if (userLanguage) {
      handleLanguage(userLanguage);
    } else {
      handleLanguage(organization?.language);
    }
  }, [userLanguage]);

  useEffect(() => {
    chaptersRef.current = chapters;
  }, [chapters]);

  const triggerUpdate = () => {
    socketRef.current.emit('trigger-survey-update', { organizationId: organization.id });
  };

  const highlightActiveSection = (userLockedSection: string) => {
    setActiveSection(userLockedSection);
    lockedSection.current = userLockedSection;
    setTimeout(() => {
      const section = document.getElementById(userLockedSection);
      section?.classList.add('highlighted-section');
    }, 100);
  };

  const getSurveyData = (disableUpdateData?: boolean, hideLoad?: boolean) => {
    setPageLoading(!hideLoad);
    dispatch(
      getOrganizationSurvey({ id, organizationId }, res => {
        setPageLoading(false);
        if (!res) {
          setNotFound(true);
        } else if (res?.survey?.chapters.length) {
          const newPages = [];
          if (!hideLoad) {
            res.survey.chapters[0].active = true;
            const newChapterData = [...res.survey.chapters];
            newChapterData.forEach(chapter => {
              let curArrIndex = 0;
              chapter.sectionsPaginated = [];
              chapter.sections.forEach((section, sectionIndex) => {
                if (section.isFromNewPage && sectionIndex > 0) {
                  curArrIndex++;
                }
                if (!chapter.sectionsPaginated[curArrIndex]) {
                  chapter.sectionsPaginated[curArrIndex] = [];
                }
                section.sectionsPaginatedIndex = curArrIndex;
                chapter.sectionsPaginated[curArrIndex].push(section);
              });
              chapter.sectionsPaginated.forEach(item => {
                newPages.push({ chapterId: chapter.id, sectionsPaginated: item });
              });
            });
            setPages(newPages);
            setChapters(newChapterData);
          }
          setSurveyName({
            name: res.survey.name,
            nameArabic: res.survey.nameArabic,
            nameChinese: res.survey.nameChinese,
            nameFrench: res.survey.nameFrench,
            nameRussian: res.survey.nameRussian,
            nameSpanish: res.survey.nameSpanish,
          });
          setOrganizationSurvey(res);
          setLockedSectionsByOthers(res.sectionLocks);
          setStatus(res.survey.status);
          setDBQuestionAnswers(JSON.parse(JSON.stringify(res.organizationQuestionAnswers)));
          setDBTabularAnswers(JSON.parse(JSON.stringify(res.organizationTabularAnswers)));
          if (!disableUpdateData) {
            setQuestionAnswers([
              ...res.organizationQuestionAnswers.filter(el => String(lockedSection.current) !== String(el.sectionId)),
              ...questionAnswersRef.current.filter(el => String(lockedSection.current) === String(el.sectionId)),
            ]);

            setTabularAnswers([
              ...res.organizationTabularAnswers.filter(el => String(lockedSection.current) !== String(el.sectionId)),
              ...tabularAnswersRef.current.filter(el => String(lockedSection.current) === String(el.sectionId)),
            ]);
          }
          setIsLockedBy(res.isLockedBy);
          if (res.userLockedSection) {
            highlightActiveSection(res.userLockedSection);
          }
        }
      }),
    );
  };

  useEffect(() => {
    dispatch(getSurveySeriesById(seriesId));
    dispatch(getStaticValues());
    if (location?.state?.message) {
      setToastType('success');
      setToast(location?.state?.message);
      window.history.replaceState({}, '');
    }
    getSurveyData();
  }, [id]);

  useEffect(() => {
    const survey = document.getElementById('survey');
    const scrollListener = () => {
      if (!TOCClickedRef.current) {
        const chapterData = handleActiveSection([...chaptersRef.current]);
        if (chapterData?.length) {
          setChapters([...chapterData]);
        }
      }
    };
    survey.addEventListener('scroll', scrollListener);

    return () => {
      survey.removeEventListener('scroll', scrollListener);
    };
  }, []);

  const handleScrollOnClick = (chapterId: string, sectionId?: string) => {
    let section;
    if (!isSameData) {
      if (!sectionId) {
        if (!isParallelFilling) {
          setSaveOrResetChangesPopup({ chapterId, sectionId: null });
        }
        return;
      } else {
        section = getSurveyItem(chapters, chapterId, sectionId) as SectionProps;
        const pageIndex = pages.findIndex(
          item => item.chapterId === chapterId && item.sectionsPaginated.find(el => el.id === sectionId),
        );
        if (pageIndex + 1 !== activePage && !isParallelFilling) {
          setSaveOrResetChangesPopup({ chapterId, sectionId });
          return;
        }
      }
    }
    TOCClickedRef.current = true;
    const { idToScrollTo, chapters: newChapterData } = handleOnScroll(chapterId, [...chapters], sectionId);
    setChapters([...newChapterData]);
    if (!sectionId) {
      setCurrentSectionIndex(0);
      const pageIndex = pages.findIndex(item => item.chapterId === chapterId);
      setActivePage(pageIndex + 1);
    } else {
      if (!section) {
        section = getSurveyItem(chapters, chapterId, sectionId) as SectionProps;
      }
      const pageIndex = pages.findIndex(
        item => item.chapterId === chapterId && item.sectionsPaginated.find(el => el.id === sectionId),
      );
      setActivePage(pageIndex + 1);
      setCurrentSectionIndex(section?.sectionsPaginatedIndex || 0);
    }

    setTimeout(() => {
      const elTOC = document.getElementById(`TOC_${idToScrollTo}`);
      if (elTOC) {
        elTOC.scrollIntoView({ behavior: 'smooth' });
      }
    }, 10);

    setTimeout(() => {
      const el = document.getElementById(idToScrollTo);
      // const position = el.getBoundingClientRect().top;

      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        // window.scrollTo({
        //   behavior: 'smooth',
        //   top: position + window.scrollY - (headerHeight + 6),
        // });
      }
    }, 500);

    setTimeout(() => {
      TOCClickedRef.current = false;
    }, 1000);
  };

  const getActiveChapterIndex = () => {
    const chapterIndex = chapters.findIndex(el => el.active);
    return chapterIndex;
  };

  const handleSubmit = () => {
    const { filledPercentage } = validateUserSurveyAnswers(chapters, questionAnswers, tabularAnswers);
    if (filledPercentage < MIN_PERCENTAGE_FILLED) {
      setToastType('error');
      setToast(submitErrorMessage);
      return;
    }
    setConfirmationPopupText('Are you sure you want to submit this survey?');
  };

  const handleMultipleChoiceRelations = (
    newQuestionAnswers: QuestionAnswer[],
    interrelatedFields?: InterrelatedFieldProps[],
  ) => {
    if (interrelatedFields?.length) {
      const optionIds = Array.from(new Set(interrelatedFields.map(el => el.optionId)));
      const toBeDeleted = [];
      const toBeKeeped = [];
      optionIds.forEach(optionId => {
        const question = newQuestionAnswers.find(el => el.optionId === optionId);
        const interrelatedFieldItems = interrelatedFields.filter(el => el.optionId === optionId);
        if (question) {
          interrelatedFieldItems.forEach(item => {
            if (!toBeDeleted.includes(item.fieldBlockId)) {
              toBeKeeped.push(item.fieldBlockId);
            }
          });
        }
      });
      optionIds.forEach(optionId => {
        const question = newQuestionAnswers.find(el => el.optionId === optionId);
        const interrelatedFieldItems = interrelatedFields.filter(el => el.optionId === optionId);
        if (!question) {
          interrelatedFieldItems.forEach(item => {
            if (!toBeKeeped.includes(item.fieldBlockId)) {
              toBeDeleted.push(item.fieldBlockId);
            }
          });
        }
      });
      if (toBeDeleted.length) {
        newQuestionAnswers = newQuestionAnswers.filter(el => !toBeDeleted.includes(el.questionId));
      }
    }
    return newQuestionAnswers;
  };

  const lockSection = (sectionId: string) => {
    if (lockedSection.current === sectionId) {
      return;
    }
    lockedSection.current = sectionId;
    socketRef.current.emit('section-lock', {
      sectionId,
      organizationId: organization.id,
      organizationSurveyId: organizationSurvey?.id,
    });
  };

  const unlockSection = (sectionId: string) => {
    if (!sectionId) {
      return;
    }
    if (lockedSection.current === sectionId) {
      lockedSection.current = null;
    }
    socketRef.current.emit('section-unlock', {
      sectionId,
      organizationId: organization.id,
      organizationSurveyId: organizationSurvey?.id,
    });
  };

  const changeQuestionAnswer = (
    value: string,
    blockFieldType: string,
    blockId: string,
    sectionId: string,
    optionId?: string,
    interrelatedFields?: InterrelatedFieldProps[],
  ) => {
    let newQuestionAnswers = [...questionAnswers];
    let questionAnswer: QuestionAnswer;
    if (blockFieldType === FREE_TEXT) {
      questionAnswer = newQuestionAnswers.find(el => el.questionId === blockId);
      if (questionAnswer) {
        if (value) {
          questionAnswer.answer = value;
        } else {
          newQuestionAnswers = newQuestionAnswers.filter(el => el.questionId !== blockId);
        }
      } else {
        newQuestionAnswers.push({ answer: value, questionId: blockId, sectionId });
      }
    } else if (blockFieldType === RADIO) {
      if (interrelatedFields?.length) {
        newQuestionAnswers = newQuestionAnswers.filter(
          el => !interrelatedFields.find(item => item.fieldBlockId === el.questionId),
        );
      }
      questionAnswer = newQuestionAnswers.find(el => el.questionId === blockId);
      if (questionAnswer) {
        if (optionId) {
          questionAnswer.optionId = optionId;
          questionAnswer.answer = '';
        } else if (value) {
          questionAnswer.answer = value;
          questionAnswer.optionId = null;
        } else {
          newQuestionAnswers = newQuestionAnswers.filter(el => el.questionId !== blockId);
        }
      } else {
        newQuestionAnswers.push({ questionId: blockId, optionId, answer: value, sectionId });
      }
    } else if (blockFieldType === CHECKBOX) {
      if (value) {
        questionAnswer = newQuestionAnswers.find(el => blockId === el.questionId && el.answer);
        if (questionAnswer) {
          questionAnswer.answer = value;
        } else {
          newQuestionAnswers.push({ questionId: blockId, answer: value, sectionId });
        }
      } else if (!optionId) {
        // in case of unchecking other option
        newQuestionAnswers = newQuestionAnswers.filter(el => el.questionId !== blockId || el.optionId);
      } else {
        questionAnswer = newQuestionAnswers.find(el => el.questionId === blockId && el.optionId === optionId);
        if (questionAnswer) {
          newQuestionAnswers = newQuestionAnswers.filter(el => el.questionId !== blockId || el.optionId !== optionId);
        } else {
          newQuestionAnswers.push({ questionId: blockId, optionId, sectionId });
        }
      }
      newQuestionAnswers = handleMultipleChoiceRelations(newQuestionAnswers, interrelatedFields);
    }
    setQuestionAnswers([...newQuestionAnswers]);
  };

  const changeTabularAnswer = (
    value: string | boolean,
    columnType: string,
    sectionId: string,
    columnId: string,
    rowId: string,
    rowKey: 'rowId' | 'organizationRowId',
    matrixType?: 'Radio' | 'Checkbox',
  ) => {
    let newTabularAnswers = [...tabularAnswers];
    let newTabularAnswer: TabularAnswer;
    if (columnType === FREE_TEXT) {
      if (rowKey === 'rowId' && matrixType === 'Radio') {
        newTabularAnswers = newTabularAnswers.filter(el => el[rowKey] !== rowId || el.answer);
      }
      newTabularAnswer = newTabularAnswers.find(
        el => el.sectionId === sectionId && el.columnId === columnId && el[rowKey] === rowId,
      );
      if (newTabularAnswer) {
        if (value) {
          newTabularAnswer.answer = value as string;
        } else {
          newTabularAnswers = newTabularAnswers.filter(
            el => el.sectionId !== sectionId || el[rowKey] !== rowId || el.columnId !== columnId,
          );
        }
      } else {
        newTabularAnswers.push({ answer: value as string, sectionId, columnId, [rowKey]: rowId });
      }
    } else if (columnType === RADIO) {
      newTabularAnswers = newTabularAnswers.filter(el => el[rowKey] !== rowId);
      newTabularAnswers.push({ sectionId, columnId, [rowKey]: rowId, checked: true });
    } else if (columnType === CHECKBOX) {
      newTabularAnswer = newTabularAnswers.find(
        el => el.sectionId === sectionId && el.columnId === columnId && el[rowKey] === rowId,
      );
      if (newTabularAnswer) {
        if (value) {
          newTabularAnswer.checked = value as boolean;
        } else {
          newTabularAnswers = newTabularAnswers.filter(
            el => el.sectionId !== sectionId || el[rowKey] !== rowId || el.columnId !== columnId,
          );
        }
      } else {
        newTabularAnswers.push({ sectionId, columnId, [rowKey]: rowId, checked: true });
      }
    }
    setTabularAnswers([...newTabularAnswers]);
  };

  const saveAsDraft = (callback?: () => void, disableUpdateData?: boolean, shouldTriggerUpdate?: boolean) => {
    setLoading(true);
    const questionAnswerData = questionAnswers.map(questionAnswer => ({
      ...questionAnswer,
      organizationSurveyId: organizationSurvey?.id,
    }));
    const tabularAnswerData = tabularAnswers.map(questionAnswer => ({
      ...questionAnswer,
      organizationSurveyId: organizationSurvey?.id,
    }));
    dispatch(
      createOrganizationSurveyAnswers(
        { questionAnswers: questionAnswerData, tabularAnswers: tabularAnswerData, id },
        (res, message) => {
          setLoading(false);
          if (res) {
            setToastType('success');
            setToast('Survey is saved.');
            getSurveyData(disableUpdateData, true);
            if (shouldTriggerUpdate) {
              triggerUpdate();
            }
            if (callback) {
              setTimeout(() => {
                callback();
              }, 1000);
            }
          } else {
            setToastType('error');
            setToast(message || 'Opps, unknown error occured.');
          }
        },
      ),
    );
  };

  const startFilling = () => {
    dispatch(
      handleSurveyLock({ id: organizationSurvey?.id, type: LOCK }, (res, message) => {
        if (res) {
          setToastType('success');
          setToast('Survey is locked. You may start filling.');
          setIsLockedBy(res.isLockedBy);
          setFilling(true);
        } else {
          setToastType('error');
          setToast(message || 'Opps, unknown error occured.');
        }
      }),
    );
  };

  const stopFilling = () => {
    dispatch(
      handleSurveyLock({ id: organizationSurvey?.id, type: UNLOCK }, (res, message) => {
        if (res) {
          setToastType('success');
          setToast('Survey filling is finished so it is unlocked.');
          setIsLockedBy(res.isLockedBy);
          setFilling(false);
        } else {
          setToastType('error');
          setToast(message || 'Opps, unknown error occured.');
        }
      }),
    );
  };

  const closeConfirmationPopup = () => {
    setConfirmationPopupText('');
  };

  const submit = () => {
    setConfirmLoading(true);
    dispatch(
      submitSurvey(organizationSurvey?.id, (res, message) => {
        setConfirmLoading(false);
        closeConfirmationPopup();
        if (res) {
          setToastType('success');
          setToast('Survey submitted.');
          getSurveyData();
        } else {
          setToastType('error');
          setToast(message || 'Opps, unknown error occured.');
        }
      }),
    );
  };

  const unlockBlock = () => {
    if (blocker?.state === 'blocked') {
      blocker.proceed();
    }
  };

  const removeSectionHighlight = () => {
    if (activeSection && blocker?.state !== 'blocked') {
      const section = document.getElementById(activeSection);
      section?.classList.remove('highlighted-section');
      if (!isSameData) {
        setToastBackgroundHidden(true);
        setTimeout(() => {
          saveAsDraft(null, lockedSection.current !== activeSection, true);
          unlockSection(activeSection);
        }, 10);
      } else {
        unlockSection(activeSection);
      }
      setActiveSection(null);
    }
  };

  const highlightSection = (e: React.SyntheticEvent, sectionId: string) => {
    e.stopPropagation();
    if (activeSection !== sectionId) {
      removeSectionHighlight();
      setActiveSection(sectionId);
      const section = document.getElementById(sectionId);
      section?.classList.add('highlighted-section');
      lockSection(sectionId);
    }
  };

  const hideButtons = status === CLOSED || organizationSurvey.progress === SUBMITTED || isAdmin;

  const isLocked = isLockedBy && isLockedBy !== userId;

  const readonly = status === CLOSED || organizationSurvey.progress === SUBMITTED || isLocked;

  const isLastPage =
    getActiveChapterIndex() + 1 === chapters.length &&
    currentSectionIndex + 1 === chapters[chapters.length - 1].sectionsPaginated.length;

  const isParallelFilling = organizationSurvey.organization?.parallelFilling;

  if (notFound) {
    return <NoData message='No survey found' />;
  }
  if (pageLoading) {
    return (
      <Box className='loader'>
        <CircularProgress size={40} color='inherit' />
      </Box>
    );
  }

  return (
    <>
      <Box
        className='survey-container'
        onClick={() => {
          if (isParallelFilling) {
            removeSectionHighlight();
          }
        }}
      >
        <Box>
          <Box display='flex' marginBottom={6}>
            {isAdmin && (
              <Box>
                <Typography mb={2}>Country: {organizationSurvey.organization?.country?.name || 'N/A'}</Typography>
                <Typography>Organization: {organizationSurvey.organization?.name || 'N/A'}</Typography>
              </Box>
            )}
            <Typography variant='h5' textAlign='center' className='title'>
              {organizationSurvey.survey?.surveySeriesName} (
              {language === 'English' || !surveyName[`name${language}`]
                ? surveyName['name']
                : surveyName[`name${language}`]}
              )
            </Typography>
            {!!organizationSurvey.progress && isAdmin && (
              <Box marginLeft='auto'>
                <Chip
                  label={organizationSurvey.progress}
                  color={
                    organizationSurvey.progress === IN_PROGRESS
                      ? 'primary'
                      : organizationSurvey.progress === SUBMITTED
                        ? 'success'
                        : 'info'
                  }
                  sx={{ width: 110, fontSize: 15 }}
                />
              </Box>
            )}
          </Box>
          <Box className='survey-top-container'>
            <Box>
              <Typography variant='body1' textAlign='left' alignContent='center'>
                Series: {surveySeries?.name}
              </Typography>
              {organizationSurvey.survey?.notes && (
                <Typography variant='body1' textAlign='left' alignContent='center' fontStyle='italic'>
                  Note: <NoteContent content={organizationSurvey.survey.notes} />
                </Typography>
              )}
            </Box>

            <Box display='flex' gap={3}>
              {!isAdmin && organizationSurvey?.progress !== SUBMITTED && organizationSurvey?.lastSurveys?.length && (
                <>
                  <Switch
                    label={
                      <>
                        <Typography>Show last surveys` answers</Typography>
                      </>
                    }
                    checked={showLastSurvey}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setShowLastSurvey(e.target.checked)}
                    size='small'
                  />
                </>
              )}
              <Dropdown
                value={language}
                label='Language'
                options={languages}
                onChange={(_: SyntheticEvent, value: string) => handleLanguage(value)}
                disableClearable
              />
            </Box>
          </Box>
        </Box>
        <Box className='survey-inner-container'>
          {showToc && (
            <Box className='toc-container'>
              <Typography className='toc-heading'>TOC</Typography>
              <List className='toc-list' sx={verticalScrollStyle} style={{ height: '62vh' }}>
                {chapters.map(chapter => (
                  <Box key={chapter.id}>
                    <ListItem>
                      <ListItemText
                        id={`TOC_${chapter.id}`}
                        className='toc-item-text'
                        primary={
                          <Tooltip title={chapter.name} placement='top-start'>
                            <Typography
                              variant='body1'
                              noWrap
                              onClick={() => handleScrollOnClick(chapter.id)}
                              sx={chapter.active ? { fontWeight: 'bold' } : {}}
                            >
                              {chapter.order}{' '}
                              {language === 'English' || !chapter[`name${language}`]
                                ? chapter.name
                                : `${chapter[`name${language}`] || 'N/A'}`}
                            </Typography>
                          </Tooltip>
                        }
                      />
                    </ListItem>
                    <List sx={{ mb: 2.5 }}>
                      {chapter.sections.map(section => (
                        <ListItem key={section.id}>
                          <ListItemText
                            id={`TOC_${section.id}`}
                            className='toc-item-text'
                            primary={
                              <Tooltip title={section.name} placement='top-start'>
                                <Typography
                                  variant='caption'
                                  noWrap
                                  onClick={() => handleScrollOnClick(chapter.id, section.id)}
                                  sx={{
                                    fontWeight: chapter.active && section.active ? 'bold' : 'unset',
                                    marginLeft: '20px',
                                  }}
                                >
                                  {chapter.order}.{section.order}{' '}
                                  {language === 'English' || !section[`name${language}`]
                                    ? section.name
                                    : `${section[`name${language}`]}`}
                                </Typography>
                              </Tooltip>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </List>
            </Box>
          )}
          <Box
            className='survey'
            id='survey'
            sx={verticalScrollStyle}
            style={{
              height: '67vh',
              cursor: isAdmin ? 'unset' : readonly || (!filling && !isParallelFilling) ? 'not-allowed' : 'pointer',
            }}
          >
            {chapters
              .filter(el => el.active)
              .map(chapter => (
                <Card
                  key={chapter.id}
                  className='chapter'
                  id={chapter.id}
                  style={readonly || (!filling && !isParallelFilling) || isAdmin ? { pointerEvents: 'none' } : {}}
                >
                  <Box display='flex'>
                    <Typography variant='h6'>
                      {chapter.order}{' '}
                      {language === 'English' || !chapter[`name${language}`]
                        ? chapter.name
                        : `${chapter[`name${language}`]}`}
                    </Typography>
                  </Box>
                  {chapter.sectionsPaginated?.[currentSectionIndex]?.map(section => (
                    <Card
                      style={
                        lockedSectionsByOthers.includes(section.id) && !isAdmin
                          ? { opacity: '0.5', pointerEvents: 'none' }
                          : {}
                      }
                      variant='outlined'
                      key={section.id}
                      className='section-container'
                      id={section.id}
                      onClick={e => {
                        if (
                          isParallelFilling &&
                          !lockedSectionsByOthers.includes(section.id) &&
                          !readonly &&
                          !isAdmin
                        ) {
                          highlightSection(e, section.id);
                        }
                      }}
                    >
                      <CardContent>
                        <Box sx={{ mb: 2.5 }} display='flex' justifyContent='space-between'>
                          <Typography>
                            {chapter.order}.{section.order}{' '}
                            {language === 'English' || !section[`name${language}`]
                              ? section.name
                              : `${section[`name${language}`]}`}
                          </Typography>
                          {lockedSectionsByOthers.includes(section.id) && !isAdmin && <Typography>Locked</Typography>}
                        </Box>
                        {section.fieldBlocks.map((block, index) => (
                          <UserFieldBlock
                            key={index}
                            block={block}
                            section={section}
                            chapter={chapter}
                            index={index}
                            language={language}
                            chapters={chapters}
                            setChapters={setChapters}
                            changeQuestionAnswer={changeQuestionAnswer}
                            questionAnswers={questionAnswers.filter(el => el.questionId === block.id)}
                            allQuestionAnswers={questionAnswers}
                            tabularAnswers={tabularAnswers.filter(el => el.sectionId === section.id)}
                            changeTabularAnswer={changeTabularAnswer}
                            setTabularAnswers={setTabularAnswers}
                            readonly={
                              readonly ||
                              (!filling && !isParallelFilling) ||
                              lockedSectionsByOthers.includes(section.id)
                            }
                            showLastSurvey={showLastSurvey}
                            isAdmin={isAdmin}
                            lastSurveys={organizationSurvey?.lastSurveys}
                          />
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                  <Box className='pagination-container' display='flex' sx={{ backgroundColor: '#fff' }}>
                    <Typography variant='caption' alignContent='center'>
                      Pages
                    </Typography>
                    <Pagination
                      count={pages.length}
                      onChange={(_, page) => {
                        setActivePage(page);
                        const newPage = pages[page - 1];
                        handleScrollOnClick(newPage.chapterId, newPage.sectionsPaginated?.[0]?.id);
                      }}
                      page={activePage}
                    />
                    <TOCButton
                      showToc={showToc}
                      onClick={() =>
                        requestAnimationFrame(() => {
                          setShowToc(prev => !prev);
                        })
                      }
                    />
                    {/* {showLastSurvey && !isAdmin && (
                    <Box marginLeft={2} display="flex" alignItems="center">
                      <Typography variant="h5" display="inline" color="green">
                        *
                      </Typography>
                      <Typography
                        variant="caption"
                        display="inline"
                        marginLeft={0.5}
                      >
                        Selection from last survey
                      </Typography>
                    </Box>
                  )} */}
                  </Box>
                </Card>
              ))}
          </Box>
        </Box>
        {/* <Box className="pagination-container">
        <Pagination
          count={chapters.length}
          onChange={(_, page) => handleScrollOnClick(chapters[page - 1].id)}
          page={getActiveChapterIndex() + 1}
        />
      </Box> */}

        <Box className='submit-button'>
          <>
            <Button
              text='Export as PDF'
              onClick={() => setShowExportPopup(!showExportPopup)}
              disabled={!id}
              variant='outlined'
            />
            <ExportModal
              showExportPopup={showExportPopup}
              setShowExportPopup={setShowExportPopup}
              organizationId={organization?.id || organizationSurvey?.organization?.id}
              surveyId={id}
            />
            {!hideButtons && !filling && !isParallelFilling && (
              <Button
                text='Start filling'
                onClick={startFilling}
                color='secondary'
                disabled={isLocked}
                tooltip={
                  isLocked
                    ? `This survey is being filled by user with email ${organizationSurvey?.isLockedByUser?.email || 'N/A'} and name  ${organizationSurvey?.isLockedByUser?.name || 'N/A'}`
                    : ''
                }
                style={isLocked ? { pointerEvents: 'auto' } : {}}
              />
            )}
            {filling && !isParallelFilling && (
              <Button
                text='Finish filling'
                onClick={stopFilling}
                color='secondary'
                disabled={!isSameData}
                tooltip={
                  !isSameData ? 'You have unsaved changes, please save them as draft and only then finish filling.' : ''
                }
                style={!isSameData ? { pointerEvents: 'auto' } : {}}
              />
            )}
            {filling && <Button text='Save as draft' onClick={saveAsDraft} loading={loading} />}
            {!filling && isLastPage && (
              <Button
                text={organizationSurvey?.progress === SUBMITTED ? SUBMITTED : 'Submit'}
                onClick={handleSubmit}
                color='success'
                disabled={
                  isLocked ||
                  !!activeSection ||
                  !!lockedSectionsByOthers?.length ||
                  organizationSurvey?.progress === SUBMITTED
                }
                tooltip={
                  isLocked
                    ? `This survey is being filled by user with email ${organizationSurvey?.isLockedByUser?.email || 'N/A'} and name  ${organizationSurvey?.isLockedByUser?.name || 'N/A'}`
                    : ''
                }
                style={isLocked ? { pointerEvents: 'auto' } : {}}
              />
            )}
          </>
        </Box>
        <ToastMessage
          toast={toast}
          close={() => {
            setToast('');
            setToastBackgroundHidden(false);
          }}
          messageType={toastType || 'success'}
          autoHideDuration={toastBackgroundHidden ? 1000 : null}
          backgroundHidden={toastBackgroundHidden}
        />
        <SaveOrResetChanges
          open={(!!saveOrResetChangesPopup || blocker?.state === 'blocked') && !toast}
          close={() => {
            if (loading) {
              return;
            }
            if (blocker?.state === 'blocked') {
              blocker.reset();
            } else {
              setSaveOrResetChangesPopup(null);
            }
          }}
          language={userLanguage}
          save={() => {
            saveAsDraft(unlockBlock);
            unlockSection(activeSection);
          }}
          discard={() => {
            if (loading) {
              return;
            }
            setQuestionAnswers(JSON.parse(JSON.stringify(DBQuestionAnswers)));
            setTabularAnswers(JSON.parse(JSON.stringify(DBTabularAnswers)));
            unlockBlock();
            unlockSection(activeSection);
          }}
          loading={loading}
        />
        <Confirmation
          confirm={submit}
          text={confirmationPopupText}
          close={closeConfirmationPopup}
          loading={confirmLoading}
        />
      </Box>
      <SaveOrRefresh
        saveOrRefreshPopup={saveOrRefreshPopup}
        save={() => {
          removeSectionHighlight();
          setSaveOrRefreshPopup('');
        }}
        refresh={() => {
          window.location.reload();
        }}
      />
    </>
  );
}

export default UserSurvey;
